// combatpro/views/AbilitiesView.js
//
// Stage 33: view-abilities shell. Stage 68 (Abilities View Foundation) migrates the
// legacy per-monster Abilities card (battletracker.html actions-block, 6006-6036,
// renderer refreshAbilities 8118, add/remove flows 8036-8116) for the ACTIVE monster.
//
// The legacy built one actions-block-${i} per monster inside createMonsterPanel and
// reparented the active one into #vi-actions-mount (the legacy id quirk: the abilities
// mount is named vi-actions-mount, preserved). React renders the active monster's card
// directly from state.currentCombatantIndex (the MoraleView / AbilityScoresView
// pattern) - no reparenting, no getElementById.
//
// Faithful migration (master spec preserved verbatim):
//   - the section header (✨ Abilities) and the two static guide cards
//     (How Abilities Work / How to Use), 6008-6030.
//   - the abilities list (refreshAbilities 8118): empty -> "No abilities"; else one
//     row per monster.abilities entry with "⚡ name", a Custom badge when
//     ability.custom, a description toggle (desc from the entry or resolved via the
//     official ability DB, else "No description available"), and a Remove button.
//   - the "+ Add Ability" picker (addAbility 8036 / confirmAddAbility 8068): a select
//     of [official DB + view-local custom abilities] sorted by name plus a
//     "+ Add Custom Ability" option; Add resolves the choice and commits, Cancel
//     closes.
//
// Ownership: monster state is owned by CombatContext. Entry add/remove go through the
// Stage 68 addMonsterAbility / removeMonsterAbility handlers (the nested abilities
// writes updateMonsterField cannot express). The official ability database is reused
// from window.VI_ABILITIES_DB (the same global the Stage 61 selection runtime reads via
// readAbilitiesDb) - not re-bundled. View-local state owns only UI concerns: the picker
// visibility + selected value, the session custom-ability accumulator (the legacy
// customAbilities picker list, a UI dropdown source - not shared state), and the set of
// expanded description ids (the legacy toggleAbilityDesc show/hide). Custom-ability
// entry keeps the legacy window.prompt interaction; removal keeps window.confirm. No
// DOM manipulation, no getElementById, no createElement, no innerHTML, no new runtime,
// no CombatContext ownership change. No optional chaining. No em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';

// Read the official ability database the same way the Stage 61 runtime does.
function readAbilitiesDb() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_ABILITIES_DB)) ? window.VI_ABILITIES_DB : [];
}

function abilityNameOf(a) {
  return (typeof a === 'string') ? a : (a && a.name) || '';
}

function AbilitiesView() {
  const { state, addMonsterAbility, removeMonsterAbility } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  // View-local UI state (not shared application state).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState('');
  const [customAbilities, setCustomAbilities] = useState([]);
  const [expanded, setExpanded] = useState({});

  if (!monster) {
    return (
      <div className="vi-view" id="view-abilities">
        <div id="vi-actions-mount"></div>
      </div>
    );
  }

  const abilities = Array.isArray(monster.abilities) ? monster.abilities : [];
  const db = readAbilitiesDb();

  // Picker option list: [official DB + custom] sorted by name, then the custom
  // sentinel (addAbility 8037-8047).
  const allForPicker = db.concat(customAbilities).slice().sort(function(a, b) {
    return abilityNameOf(a).localeCompare(abilityNameOf(b));
  });

  function openPicker() {
    var first = allForPicker.length ? abilityNameOf(allForPicker[0]) : '__CUSTOM__';
    setPickerValue(first);
    setPickerOpen(true);
  }

  function closePicker() {
    setPickerOpen(false);
  }

  // confirmAddAbility (8068): custom path uses two prompts; otherwise resolve the
  // chosen entry from the DB / custom list and commit through the handler.
  function confirmAdd() {
    var abilityName = pickerValue;
    if (abilityName === '__CUSTOM__') {
      var customName = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Enter custom ability name:') : null;
      if (!customName || !customName.trim()) { closePicker(); return; }
      abilityName = customName.trim();
      var customDesc = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Enter ability description (optional):') : null;
      var customAbility = {
        name: abilityName,
        desc: (customDesc && customDesc.trim()) ? customDesc.trim() : 'Custom ability - no description provided.',
        custom: true
      };
      setCustomAbilities(function(prev) {
        var present = prev.some(function(a) { return abilityNameOf(a) === abilityName; });
        return present ? prev : prev.concat([customAbility]);
      });
      addMonsterAbility(index, customAbility);
    } else {
      var found = null;
      for (var i = 0; i < db.length; i++) {
        if (db[i] && db[i].name === abilityName) { found = db[i]; break; }
      }
      if (!found) {
        for (var j = 0; j < customAbilities.length; j++) {
          if (abilityNameOf(customAbilities[j]) === abilityName) { found = customAbilities[j]; break; }
        }
      }
      if (found) { addMonsterAbility(index, found); }
    }
    closePicker();
  }

  function onRemove(abilityIndex) {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Remove this ability?')) { return; }
    removeMonsterAbility(index, abilityIndex);
  }

  function toggleDesc(id) {
    setExpanded(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = !prev[id];
      return next;
    });
  }

  // Resolve an entry's description (refreshAbilities 8128-8137): entry desc, else the
  // official DB description, else none.
  function resolveDescription(ability) {
    var abilityDesc = (typeof ability === 'string') ? null : ability.desc;
    if (abilityDesc) { return abilityDesc; }
    var name = abilityNameOf(ability);
    for (var i = 0; i < db.length; i++) {
      if (db[i] && db[i].name === name) { return db[i].desc; }
    }
    return null;
  }

  return (
    <div className="vi-view" id="view-abilities">
      <div id="vi-actions-mount">
        <div id={'actions-block-' + index}>
          <div className="abilities-section" id={'abilities-section-' + index}>
            <div className="vi-section-header">
              <h3>✨ Abilities</h3>
            </div>
            <div className="vi-section-body">
              <div className="vi-ability-helper-grid vi-abilities-cards">
                <div className="vi-ability-card">
                  <div className="vi-ability-title">How Abilities Work</div>
                  <p>Abilities define special actions, traits, or effects a monster can use during combat.</p>
                  <p>Passive abilities apply automatically. Active abilities appear in combat and are triggered when used.</p>
                </div>
                <div className="vi-ability-card">
                  <div className="vi-ability-title">How to Use</div>
                  <div className="vi-howto-steps">
                    <p>Click <strong>Add Ability</strong>.</p>
                    <p>Enter the ability name and effect.</p>
                    <p>The ability will appear in combat when available.</p>
                  </div>
                </div>
              </div>

              <div id={'abilities-list-' + index} className="vi-mb-10">
                {abilities.length === 0 ? (
                  <p className="vi-modal-result-text">No abilities</p>
                ) : (
                  abilities.map(function(ability, abilityIndex) {
                    var name = abilityNameOf(ability);
                    var isCustom = (typeof ability === 'object') && ability.custom;
                    var description = resolveDescription(ability);
                    var uniqueId = 'ability-' + index + '-' + abilityIndex;
                    var isOpen = !!expanded[uniqueId];
                    return (
                      <div
                        key={uniqueId}
                        style={{ background: 'var(--card-bg)', marginBottom: '8px', borderRadius: 'var(--radiusSm)', borderLeft: '3px solid var(--gold)', border: '1px solid var(--cp-border)' }}
                      >
                        <div className="vi-ability-entry-row">
                          <div className="vi-flex-1">
                            <div className="vi-ability-name-row">
                              <span className="vi-ability-name">{'\u26a1 ' + name}</span>
                              {isCustom ? <span className="vi-custom-badge">Custom</span> : null}
                            </div>
                            {description ? (
                              <>
                                <button type="button" onClick={function() { toggleDesc(uniqueId); }} className="vi-btn vi-spell-toggle-btn">
                                  <span id={uniqueId + '-toggle'}>{isOpen ? '\u25b2 Hide Description' : '\u25bc Show Description'}</span>
                                </button>
                                <div id={uniqueId} className="vi-spell-desc-hidden" style={{ display: isOpen ? 'block' : 'none' }}>
                                  {description}
                                </div>
                              </>
                            ) : (
                              <div className="vi-spell-no-desc">No description available</div>
                            )}
                          </div>
                          <button type="button" onClick={function() { onRemove(abilityIndex); }} className="btn-danger vi-btn vi-btn-sm">Remove</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <button type="button" onClick={openPicker} className="vi-add-ability-btn">+ Add Ability</button>
            </div>
          </div>
        </div>
      </div>

      {pickerOpen ? (
        <div className="ability-modal-root">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box-sm">
              <h3 className="vi-modal-title">Select Ability</h3>
              <select id="abilityDropdown" className="vi-modal-select" value={pickerValue} onChange={function(e) { setPickerValue(e.target.value); }}>
                {allForPicker.map(function(ab) {
                  var name = abilityNameOf(ab);
                  return <option key={name} value={name}>{name}</option>;
                })}
                <option value="__CUSTOM__">+ Add Custom Ability</option>
              </select>
              <div className="vi-modal-actions">
                <button type="button" onClick={closePicker} className="vi-btn vi-btn-secondary">Cancel</button>
                <button type="button" onClick={confirmAdd} className="btn-danger vi-btn">Add</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AbilitiesView;
